import { format, addMinutes, isWithinInterval, parseISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { ru } from "date-fns/locale";

export const MOSCOW_TZ = "Europe/Moscow";
export const DEFAULT_BUSINESS_TIME_ZONE = "Asia/Omsk";

const RUSSIAN_CITY_TIME_ZONES: Record<string, string> = {
  "абакан": "Asia/Krasnoyarsk",
  "анадырь": "Asia/Anadyr",
  "ангарск": "Asia/Irkutsk",
  "архангельск": "Europe/Moscow",
  "астрахань": "Europe/Astrakhan",
  "барнаул": "Asia/Barnaul",
  "бийск": "Asia/Barnaul",
  "биробиджан": "Asia/Vladivostok",
  "благовещенск": "Asia/Yakutsk",
  "братск": "Asia/Irkutsk",
  "брянск": "Europe/Moscow",
  "великий новгород": "Europe/Moscow",
  "владивосток": "Asia/Vladivostok",
  "владикавказ": "Europe/Moscow",
  "владимир": "Europe/Moscow",
  "волгоград": "Europe/Volgograd",
  "вологда": "Europe/Moscow",
  "воронеж": "Europe/Moscow",
  "гатчина": "Europe/Moscow",
  "грозный": "Europe/Moscow",
  "екатеринбург": "Asia/Yekaterinburg",
  "ижевск": "Europe/Samara",
  "иркутск": "Asia/Irkutsk",
  "йошкар ола": "Europe/Moscow",
  "казань": "Europe/Moscow",
  "калининград": "Europe/Kaliningrad",
  "калуга": "Europe/Moscow",
  "каменск уральский": "Asia/Yekaterinburg",
  "кемерово": "Asia/Novokuznetsk",
  "киров": "Europe/Kirov",
  "комсомольск на амуре": "Asia/Vladivostok",
  "кострома": "Europe/Moscow",
  "краснодар": "Europe/Moscow",
  "красноярск": "Asia/Krasnoyarsk",
  "курган": "Asia/Yekaterinburg",
  "курск": "Europe/Moscow",
  "липецк": "Europe/Moscow",
  "магадан": "Asia/Magadan",
  "магнитогорск": "Asia/Yekaterinburg",
  "майкоп": "Europe/Moscow",
  "махачкала": "Europe/Moscow",
  "москва": "Europe/Moscow",
  "мурманск": "Europe/Moscow",
  "набережные челны": "Europe/Moscow",
  "находка": "Asia/Vladivostok",
  "нижневартовск": "Asia/Yekaterinburg",
  "нижний новгород": "Europe/Moscow",
  "нижний тагил": "Asia/Yekaterinburg",
  "новокузнецк": "Asia/Novokuznetsk",
  "новороссийск": "Europe/Moscow",
  "новосибирск": "Asia/Novosibirsk",
  "омск": "Asia/Omsk",
  "оренбург": "Asia/Yekaterinburg",
  "орел": "Europe/Moscow",
  "орёл": "Europe/Moscow",
  "пенза": "Europe/Moscow",
  "пермь": "Asia/Yekaterinburg",
  "петрозаводск": "Europe/Moscow",
  "петропавловск камчатский": "Asia/Kamchatka",
  "петропавловск-камчатский": "Asia/Kamchatka",
  "псков": "Europe/Moscow",
  "ростов на дону": "Europe/Moscow",
  "ростов-на-дону": "Europe/Moscow",
  "рязань": "Europe/Moscow",
  "салехард": "Asia/Yekaterinburg",
  "самара": "Europe/Samara",
  "санкт петербург": "Europe/Moscow",
  "санкт-петербург": "Europe/Moscow",
  "саранск": "Europe/Moscow",
  "саратов": "Europe/Saratov",
  "симферополь": "Europe/Moscow",
  "смоленск": "Europe/Moscow",
  "сочи": "Europe/Moscow",
  "ставрополь": "Europe/Moscow",
  "сургут": "Asia/Yekaterinburg",
  "сыктывкар": "Europe/Moscow",
  "тамбов": "Europe/Moscow",
  "тверь": "Europe/Moscow",
  "тольятти": "Europe/Samara",
  "томск": "Asia/Tomsk",
  "тула": "Europe/Moscow",
  "тюмень": "Asia/Yekaterinburg",
  "улан удэ": "Asia/Irkutsk",
  "улан-удэ": "Asia/Irkutsk",
  "ульяновск": "Europe/Ulyanovsk",
  "уссурийск": "Asia/Vladivostok",
  "уфа": "Asia/Yekaterinburg",
  "ухта": "Europe/Moscow",
  "хабаровск": "Asia/Vladivostok",
  "ханты мансийск": "Asia/Yekaterinburg",
  "ханты-мансийск": "Asia/Yekaterinburg",
  "чебоксары": "Europe/Moscow",
  "челябинск": "Asia/Yekaterinburg",
  "череповец": "Europe/Moscow",
  "чита": "Asia/Chita",
  "элиста": "Europe/Moscow",
  "южно сахалинск": "Asia/Sakhalin",
  "южно-сахалинск": "Asia/Sakhalin",
  "якутск": "Asia/Yakutsk",
  "ярославль": "Europe/Moscow",
};

const RUSSIAN_LONGITUDE_TIME_ZONES: Array<{ maxLon: number; timeZone: string }> = [
  { maxLon: 26, timeZone: "Europe/Kaliningrad" },
  { maxLon: 48, timeZone: "Europe/Moscow" },
  { maxLon: 60, timeZone: "Europe/Samara" },
  { maxLon: 76, timeZone: "Asia/Yekaterinburg" },
  { maxLon: 80, timeZone: "Asia/Omsk" },
  { maxLon: 103, timeZone: "Asia/Krasnoyarsk" },
  { maxLon: 116, timeZone: "Asia/Irkutsk" },
  { maxLon: 130, timeZone: "Asia/Yakutsk" },
  { maxLon: 145, timeZone: "Asia/Vladivostok" },
  { maxLon: 157, timeZone: "Asia/Magadan" },
  { maxLon: 170, timeZone: "Asia/Kamchatka" },
  { maxLon: 180, timeZone: "Asia/Anadyr" },
];

const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];
const WEEKDAYS_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const WEEKDAYS_LONG = [
  "воскресенье",
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота",
];

export function formatDate(date: Date, fmt = "d MMMM yyyy"): string {
  return format(date, fmt, { locale: ru });
}

export function formatDateTime(date: Date): string {
  return formatAppointmentDateTime(date);
}

export function formatTime(date: Date): string {
  return formatAppointmentTime(date);
}

export function formatAppointmentDate(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1] ?? ""}`;
}

export function formatAppointmentDateWithWeekday(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  const weekday = WEEKDAYS_SHORT[getBusinessWeekday(parts)] ?? "";
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1] ?? ""} (${weekday})`;
}

export function formatAppointmentDateWithYear(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1] ?? ""} ${parts.year}`;
}

export function formatAppointmentDateWithLongWeekday(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  const weekday = WEEKDAYS_LONG[getBusinessWeekday(parts)] ?? "";
  return `${weekday}, ${parts.day} ${MONTHS_GENITIVE[parts.month - 1] ?? ""}`;
}

export function formatAppointmentDateWithWeekdayAndYear(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  const weekday = WEEKDAYS_SHORT[getBusinessWeekday(parts)] ?? "";
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1] ?? ""} ${parts.year} (${weekday})`;
}

export function formatAppointmentTime(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

export function formatAppointmentDateTime(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  return `${formatAppointmentDate(value, timeZone)}, ${formatAppointmentTime(value, timeZone)}`;
}

export function toBusinessDateKey(
  value: Date | string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): string {
  const parts = getBusinessDateTimeParts(value, timeZone);
  return [
    String(parts.year).padStart(4, "0"),
    String(parts.month).padStart(2, "0"),
    String(parts.day).padStart(2, "0"),
  ].join("-");
}

export function businessDateTimeToDate(
  dateKey: string,
  time: string,
  timeZone = DEFAULT_BUSINESS_TIME_ZONE,
): Date {
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  return fromZonedTime(`${dateKey}T${normalizedTime}`, normalizeBusinessTimeZone(timeZone));
}

export function calcEndTime(startsAt: Date, durationMin: number): Date {
  return addMinutes(startsAt, durationMin);
}

export function isSlotWithinWorkHours(
  slotStart: Date,
  workStart: Date,
  workEnd: Date
): boolean {
  return isWithinInterval(slotStart, { start: workStart, end: workEnd });
}

export function parseDate(dateStr: string): Date {
  return parseISO(dateStr);
}

export function getDayName(dayOfWeek: number): string {
  const days = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
  return days[dayOfWeek] ?? "";
}

export function normalizeBusinessTimeZone(value: unknown): string {
  if (typeof value !== "string") return DEFAULT_BUSINESS_TIME_ZONE;
  const timeZone = value.trim();
  if (!timeZone) return DEFAULT_BUSINESS_TIME_ZONE;

  try {
    new Intl.DateTimeFormat("ru-RU", { timeZone }).format(new Date("2026-01-01T00:00:00.000Z"));
    return timeZone;
  } catch {
    return DEFAULT_BUSINESS_TIME_ZONE;
  }
}

export function inferBusinessTimeZone({
  city,
  lat,
  lon,
}: {
  city?: string | null | undefined;
  lat?: number | null | undefined;
  lon?: number | null | undefined;
}): string {
  const cityTimeZone = city ? RUSSIAN_CITY_TIME_ZONES[normalizeRussianPlaceName(city)] : undefined;
  if (cityTimeZone) return normalizeBusinessTimeZone(cityTimeZone);

  if (isRussianPoint(lat, lon)) {
    const pointTimeZone = RUSSIAN_LONGITUDE_TIME_ZONES.find((item) => Number(lon) <= item.maxLon)?.timeZone;
    if (pointTimeZone) return normalizeBusinessTimeZone(pointTimeZone);
  }

  return DEFAULT_BUSINESS_TIME_ZONE;
}

function getBusinessDateTimeParts(value: Date | string, timeZone: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} {
  const date = value instanceof Date ? value : new Date(value);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: normalizeBusinessTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return {
    year: Number(parts["year"]),
    month: Number(parts["month"]),
    day: Number(parts["day"]),
    hour: Number(parts["hour"]),
    minute: Number(parts["minute"]),
  };
}

function getBusinessWeekday(parts: { year: number; month: number; day: number }): number {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
}

function normalizeRussianPlaceName(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase("ru-RU")
    .replace(/ё/g, "е")
    .replace(/^(г\.?|город)\s+/u, "")
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isRussianPoint(lat: number | null | undefined, lon: number | null | undefined): boolean {
  if (typeof lat !== "number" || typeof lon !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return lat >= 41 && lat <= 82 && lon >= 19 && lon <= 180;
}
